import React, { useState, useEffect, useRef } from 'react';
import { toCanonicalPeriod, buildPaywallEventProps } from '@learnzapp/core/analytics';
import {
  pickDefaultPlan, getPlanBadges, getPerMonthRate, buildPlanCard, PLAN_BADGE,
  getBillingLabel, getSavingsLine, getBadgeLabel, getCtaLabel, getCtaSupportLine,
  getPerMonthLabel, getModeHint, getRenewalDisclosure, getExpiryDisclosure,
  getCancelIntro, getCancelInfo, resolveModeSwitchSelection, PROMOTED_RATIONALE,
} from '@learnzapp/core/pricing';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRotate,
  faShoppingCart,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';

// Currency symbols arrive as HTML entities (e.g. "&#36;", "&#8364;"). Decode to
// plain text so the shared @learnzapp/core copy builders can compose them into
// real strings (the CTA / subtext / support line) instead of us hand-rendering
// HTML — the price column below still renders the entity directly.
const decodeEntities = (html) => {
  if (typeof document === 'undefined') return html ?? '';
  const el = document.createElement('textarea');
  el.innerHTML = html ?? '';
  return el.value;
};
const priceText = (symbolHtml, amount) => `${decodeEntities(symbolHtml || '$')}${amount}`;

// One-time meta reads as a duration of access, never a per-month rate: "/mo" on
// a non-recurring product contradicts the toggle's "no renewal" promise. Kept
// web-only: mobile has no one-time mode (see the paywall-copy consolidation).
const accessLabel = (months) => {
  if (!months) return 'one-time';
  return `${months} ${months === 1 ? 'month' : 'months'} of access`;
};

const PricingGrid = ({ products, promoDiscount, onCheckout, theme, isInfoOnly = false, onPlanSelected, extendMode = false, footerSlot = null }) => {
  // Payment type is now a toggle (Subscription default | One-time), replacing
  // the old two-column grid. Selection persists as a canonical PERIOD so it
  // survives toggling between the two lists. In extendMode (early-renewal for an
  // active one-time buyer) only one-time terms make sense — force that mode and
  // hide the toggle below, since a recurring plan doesn't "extend" a one-time window.
  const [paymentType, setPaymentType] = useState(extendMode ? 'onetime' : 'subscription'); // 'subscription' | 'onetime'
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  // "How to cancel" opens a steps modal (linking to /profile is useless
  // pre-purchase — it just shows the free plan). Web cancels via the Stripe
  // billing portal, so the steps come from getCancelInfo('stripe').
  const [showCancel, setShowCancel] = useState(false);
  // DOM nodes per period so Arrow-key navigation can move focus (roving
  // tabindex). Rows keep stable keys within a mode, so these stay valid across
  // a selection re-render.
  const rowRefs = useRef({});

  const isOneTimeMode = paymentType === 'onetime';
  const apiTypeFor = (type) => (type === 'subscription' ? 'recurring' : 'one_time');

  // Products offered in a given mode (a period may exist in one mode only).
  const productsForMode = (type) =>
    products.filter((p) => (type === 'subscription' ? p.subscription : p.oneTime));
  const planForMode = (product, type) => (type === 'subscription' ? product?.subscription : product?.oneTime);

  const activeProducts = productsForMode(paymentType);

  // Helper functions
  const calculateDiscountedPrice = (basePrice) => {
    return basePrice - (basePrice * promoDiscount / 100);
  };

  const formatPrice = (price, currencyCode) => {
    if (!price) return '0';
    if (currencyCode === 'JPY') {
      return Math.floor(price).toString();
    }
    const formattedPrice = price / 100;
    return formattedPrice % 1 === 0 ? formattedPrice.toFixed(0) : formattedPrice.toFixed(2);
  };

  // Anchor a subscription against the monthly SUBSCRIPTION and a one-time
  // against the monthly ONE-TIME. Anchoring both on the one-time price (an
  // earlier behaviour) computed every badge off the wrong baseline and, where
  // the two differ, could brand the monthly subscription itself as a discount.
  const getBasePrice = (type) => {
    const monthlyProduct = products.find((product) => product.period === 'Monthly');
    const source = type === 'subscription'
      ? (monthlyProduct?.subscription ?? monthlyProduct?.oneTime)
      : (monthlyProduct?.oneTime ?? monthlyProduct?.subscription);
    return source?.unit_amount ?? null;
  };

  // Badges come from core so web, mobile ChoosePlan and mobile Onboarding
  // cannot drift apart. Computed from the ACTIVE mode's periods so the 4-tier
  // BEST_VALUE rule applies identically to the one-time list (a mode offering
  // fewer terms simply shows fewer badges).
  const planBadges = getPlanBadges(activeProducts.map((p) => p.period));

  // The plan we steer to. Computed from ALL products' periods so it agrees with
  // the impression event's `default_period` (choosePlans/index.js), which the
  // "accepted the default" rollout guardrail compares against — deriving it from
  // subscription-only periods here would desync the two when the top-preference
  // period is offered one-time-only.
  const steerPeriod = pickDefaultPlan(products.map((p) => p.period));

  // Pre-select the steered plan once products land. Web previously shipped with
  // NO default at all (`useState(null)` + a disabled CTA), so every purchase was
  // an unanchored choice and 62% landed on monthly.
  useEffect(() => {
    if (isInfoOnly || selectedPeriod || products.length === 0 || !steerPeriod) return;
    // Only pre-select when the steered period is actually available as a
    // subscription (the toggle defaults to subscription); otherwise leave it
    // unselected, exactly as the pre-toggle grid did.
    const target = products.find((p) => toCanonicalPeriod(p.period) === steerPeriod);
    // Pre-select only when the steered period exists in the ACTIVE mode
    // (one-time in extendMode, subscription otherwise); else leave unselected.
    if (extendMode ? !target?.oneTime : !target?.subscription) return;
    setSelectedPeriod(steerPeriod);
    // Deliberately NOT emitting plan_selected: this is our steer, not a user
    // action. "Accepted the default" is derivable as a purchase of the default
    // period carrying was_default, against the impression's default_period.
  }, [products, isInfoOnly, selectedPeriod, steerPeriod]);

  const emitSelected = (canonicalPeriod, type) => {
    if (isInfoOnly || !canonicalPeriod) return;
    const product = products.find((p) => toCanonicalPeriod(p.period) === canonicalPeriod);
    const plan = planForMode(product, type);
    onPlanSelected?.({
      // C1 block. `platform` is a distinct key from the in-app-location
      // `surface: 'web_plans'` below, so both coexist (no overload, no rename).
      ...buildPaywallEventProps({
        paymentMode: type === 'subscription' ? 'subscription' : 'one_time',
        planTerm: canonicalPeriod,
        promotedTerm: steerPeriod,
        platform: 'web',
      }),
      surface: 'web_plans',
      period: canonicalPeriod,
      payment_type: apiTypeFor(type),
      product_id: plan?.priceId ?? null,
      // True only when the user lands on the subscription plan of the period we
      // steered them to; a one-time card of the same period is an override.
      was_default: canonicalPeriod === steerPeriod && type === 'subscription',
    });
  };

  const handleSelectPeriod = (period) => {
    if (isInfoOnly) return;
    const canonical = toCanonicalPeriod(period);
    setSelectedPeriod(canonical);
    emitSelected(canonical, paymentType);
  };

  const handleToggle = (type) => {
    if (type === paymentType) return;
    setPaymentType(type);
    // Keep the current term if the target mode offers it, else steer, else first
    // row (core resolveModeSwitchSelection — pure + golden-tested).
    const period = resolveModeSwitchSelection(
      selectedPeriod,
      productsForMode(type).map((p) => p.period),
      steerPeriod,
    );
    setSelectedPeriod(period);
    // The effective plan the user would buy just changed (same period, new
    // payment type), so report it like any other selection.
    emitSelected(period, type);
  };

  // Ordered canonical periods for roving-tabindex / arrow navigation.
  const orderedPeriods = activeProducts.map((p) => toCanonicalPeriod(p.period));
  const selectedIndex = orderedPeriods.indexOf(selectedPeriod);
  const focusIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const moveSelection = (fromCanonical, dir) => {
    if (isInfoOnly || orderedPeriods.length === 0) return;
    const i = orderedPeriods.indexOf(fromCanonical);
    const base = i >= 0 ? i : 0;
    const next = orderedPeriods[(base + dir + orderedPeriods.length) % orderedPeriods.length];
    setSelectedPeriod(next);
    emitSelected(next, paymentType);
    // The target row is already mounted (stable key), so focus it now — arrow
    // navigation moves both selection and focus, per the radiogroup pattern.
    rowRefs.current[next]?.focus();
  };

  // Derived selection for the CTA.
  const selectedProduct = selectedPeriod
    ? products.find((p) => toCanonicalPeriod(p.period) === selectedPeriod)
    : null;
  const selectedPlanObj = planForMode(selectedProduct, paymentType);

  const handleCTAClick = () => {
    if (selectedPlanObj) onCheckout(selectedPlanObj.priceId, apiTypeFor(paymentType));
  };

  const accent = isOneTimeMode ? theme?.primary?.orange?.[500] : theme?.primary?.blue?.[500];

  // A plain render function (NOT a nested component): returning elements keeps
  // React reconciling by key across renders, so a selection does not remount
  // the whole list — which would destroy focus on the just-selected radio and
  // replay every row's entry animation.
  const renderRow = (product, index) => {
    const plan = planForMode(product, paymentType);
    // A period missing in this mode is simply omitted from the list.
    if (!plan) return null;

    const period = product.period;
    const canonical = toCanonicalPeriod(period);
    const originalPrice = plan.unit_amount;
    const discountedPrice = calculateDiscountedPrice(originalPrice);
    // Term discount is computed on the ORIGINAL price so it describes the plan,
    // not the promo (computing it on the discounted price double-counted the
    // promo into every "Save X%").
    const card = buildPlanCard({
      period,
      price: originalPrice,
      monthlyPrice: getBasePrice(paymentType),
      badges: planBadges,
    });
    const badge = card.badge;
    const isSelected = !isInfoOnly && selectedPeriod === canonical;
    const currencySymbol = plan.currencySymbolHtml || '$';

    // A2: per-month moves to the right column under the total; the left subtext is
    // now cadence-only (core getBillingLabel — same words mobile uses). One-time
    // stays web-specific (a duration of access, not a per-month subscription).
    const perMonthRate = getPerMonthRate(discountedPrice, card.months);
    const meta = isOneTimeMode ? accessLabel(card.months) : getBillingLabel(canonical);
    // Right-column per-month, shown for multi-month subscription terms only —
    // monthly would just repeat the total (empty slot, per spec).
    const perMonthLabel = (!isOneTimeMode && card.months > 1 && perMonthRate !== null)
      ? getPerMonthLabel(priceText(currencySymbol, formatPrice(perMonthRate, plan.currencyCode)))
      : null;
    const savingsLine = getSavingsLine(card.discountPct);
    const amountText = priceText(currencySymbol, formatPrice(discountedPrice, plan.currencyCode));
    // Announce the row as ONE option (name, price, cadence, per-month, savings),
    // not fragmented spans, to a screen reader.
    const rowAriaLabel = [period, amountText, perMonthLabel, meta, savingsLine].filter(Boolean).join(', ');

    const rowClasses = [
      'pw-row',
      isSelected ? 'pw-row--selected' : '',
      isInfoOnly ? 'pw-row--info-only' : '',
    ].filter(Boolean).join(' ');

    return (
      <motion.div
        key={`${paymentType}_${period}`}
        ref={(el) => { rowRefs.current[canonical] = el; }}
        className={rowClasses}
        onClick={isInfoOnly ? undefined : () => handleSelectPeriod(period)}
        role={isInfoOnly ? undefined : 'radio'}
        aria-checked={isInfoOnly ? undefined : isSelected}
        aria-label={isInfoOnly ? undefined : rowAriaLabel}
        tabIndex={isInfoOnly ? undefined : (index === focusIndex ? 0 : -1)}
        onKeyDown={isInfoOnly ? undefined : (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectPeriod(period); }
          else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); moveSelection(canonical, 1); }
          else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); moveSelection(canonical, -1); }
        }}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.04 * index }}
        whileTap={isInfoOnly ? {} : { scale: 0.995 }}
      >
        {!isInfoOnly && <span className="pw-row__radio" aria-hidden="true" />}

        <div className="pw-row__body">
          <div className="pw-row__top">
            <span className="pw-row__name">{period}</span>
            {badge && (
              <span className={`pw-badge pw-badge--${badge === PLAN_BADGE.BEST_VALUE ? 'best-value' : 'popular'}`}>
                {getBadgeLabel(badge)}
              </span>
            )}
          </div>
          <div className="pw-row__meta">{meta}</div>
          {savingsLine ? (
            <div className="pw-row__save">{savingsLine}</div>
          ) : null}
          {badge === PLAN_BADGE.MOST_POPULAR ? (
            <div className="pw-row__rationale">{PROMOTED_RATIONALE}</div>
          ) : null}
        </div>

        <div className="pw-row__price">
          {promoDiscount > 0 && (
            <span className="pw-row__orig">
              <span dangerouslySetInnerHTML={{ __html: currencySymbol }} />
              {formatPrice(originalPrice, plan.currencyCode)}
            </span>
          )}
          <span className="pw-row__amount">
            <span dangerouslySetInnerHTML={{ __html: currencySymbol }} />
            {formatPrice(discountedPrice, plan.currencyCode)}
          </span>
          {perMonthLabel && <span className="pw-row__permonth">{perMonthLabel}</span>}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="pricing-grid-container">
      {/* Payment-type toggle — a two-state switch, not tabs/tabpanels. Hidden in
          extendMode: an active one-time buyer can only extend with a one-time term. */}
      {!extendMode && (
      <div className="pw-toggle" role="group" aria-label="Payment type">
        <button
          type="button"
          aria-pressed={!isOneTimeMode}
          className={`pw-toggle__btn pw-toggle__btn--sub ${!isOneTimeMode ? 'is-active' : ''}`}
          onClick={() => handleToggle('subscription')}
        >
          <span className="pw-toggle__label"><FontAwesomeIcon icon={faRotate} /> Subscription</span>
          <span className="pw-toggle__sub">{getModeHint('subscription')}</span>
        </button>
        <button
          type="button"
          aria-pressed={isOneTimeMode}
          className={`pw-toggle__btn pw-toggle__btn--once ${isOneTimeMode ? 'is-active' : ''}`}
          onClick={() => handleToggle('onetime')}
        >
          <span className="pw-toggle__label"><FontAwesomeIcon icon={faShoppingCart} /> One-time</span>
          <span className="pw-toggle__sub">{getModeHint('one_time')}</span>
        </button>
      </div>
      )}

      {/* Plan list */}
      <div className="pw-list" role={isInfoOnly ? undefined : 'radiogroup'} aria-label="Plans">
        {activeProducts.map((product, index) => renderRow(product, index))}
      </div>

      {/* CTA — verb, support line and legal disclosure all swap with the payment
          mode (core builders; one-time never renders "Subscribe"). */}
      {!isInfoOnly && (() => {
        const coreMode = isOneTimeMode ? 'one_time' : 'subscription';
        // Plain-text price (decoded currency) so the shared core builders can
        // compose the label + support line as real strings.
        const ctaPrice = selectedPlanObj
          ? priceText(selectedPlanObj.currencySymbolHtml, formatPrice(calculateDiscountedPrice(selectedPlanObj.unit_amount), selectedPlanObj.currencyCode))
          : null;
        return (
        <div className="pw-cta-wrap">
          <motion.button
            type="button"
            className={`pw-cta ${!selectedPlanObj ? 'pw-cta--disabled' : ''}`}
            whileHover={selectedPlanObj ? { y: -2 } : {}}
            whileTap={selectedPlanObj ? { scale: 0.98 } : {}}
            disabled={!selectedPlanObj}
            onClick={handleCTAClick}
          >
            {selectedPlanObj ? getCtaLabel(ctaPrice, coreMode) : 'Select a Plan to Continue'}
          </motion.button>
          {selectedPlanObj && (
            <div className="pw-cta-support">{getCtaSupportLine(ctaPrice, selectedProduct?.period, coreMode)}</div>
          )}
          {/* A5: the renewal/expiry disclosure (was missing entirely) + cancel link. */}
          <div className="pw-legal">
            <p className="pw-legal__text">
              {isOneTimeMode ? getExpiryDisclosure() : getRenewalDisclosure('stripe')}
            </p>
            <div className="pw-legal__links">
              {!isOneTimeMode && (
                <>
                  <button type="button" className="pw-legal__link" onClick={() => setShowCancel(true)}>How to cancel</button>
                  <span className="pw-legal__sep" aria-hidden="true"> · </span>
                </>
              )}
              <a className="pw-legal__link" href="https://www.learnzapp.com/terms.html" target="_blank" rel="noreferrer">Terms</a>
              <span className="pw-legal__sep" aria-hidden="true"> · </span>
              <a className="pw-legal__link" href="https://www.learnzapp.com/privacy_statement.html" target="_blank" rel="noreferrer">Privacy</a>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Promo link / any trailing content, between the legal line and the Stripe
          badge (A3 order). Provided by the page so it can own the promo state. */}
      {!isInfoOnly && footerSlot}

      {!isInfoOnly && (
        <div className="pricing-grid__secure-badge">
          <div className="pricing-grid__secure-text">
            <FontAwesomeIcon icon={faShieldHalved} className="pricing-grid__secure-icon" />
            <span>Secure payment powered by</span>
            <span className="pricing-grid__stripe-badge">Stripe</span>
          </div>
        </div>
      )}

      {/* "How to cancel" steps modal (web cancels via the Stripe billing portal). */}
      {showCancel && (
        <div className="pw-cancel-overlay" onClick={() => setShowCancel(false)}>
          <div className="pw-cancel-modal" role="dialog" aria-modal="true" aria-label="How to cancel" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="pw-cancel-close" aria-label="Close" onClick={() => setShowCancel(false)}>×</button>
            <h3 className="pw-cancel-title">How to cancel</h3>
            <p className="pw-cancel-intro">{getCancelIntro()}</p>
            <ol className="pw-cancel-steps">
              {getCancelInfo('stripe').steps.map((step, i) => <li key={i}>{step}</li>)}
            </ol>
          </div>
        </div>
      )}

      <style>{`
        /* A1: no gray panel — rows sit directly on the page background (mobile
           parity), so the selected blue border/tint keeps its contrast. Padding
           only, for layout. */
        .pricing-grid-container {
          background: transparent;
          padding: 4px 0 0;
          margin-bottom: 40px;
          width: 100%;
          max-width: 560px;
          margin-left: auto;
          margin-right: auto;
          box-sizing: border-box;
        }

        /* Toggle */
        .pw-toggle {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
          background: ${theme?.backgrounds?.tertiary};
          border: 1px solid ${theme?.borders?.primary};
          border-radius: 14px;
          padding: 5px;
          margin-bottom: 18px;
        }
        .pw-toggle__btn {
          appearance: none;
          border: 1px solid transparent;
          background: transparent;
          cursor: pointer;
          border-radius: 10px;
          padding: 10px 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          font-weight: 700;
          color: ${theme?.text?.secondary};
          transition: all 0.18s ease;
        }
        .pw-toggle__label { font-size: 0.95rem; display: flex; align-items: center; gap: 7px; }
        .pw-toggle__sub { font-size: 0.68rem; font-weight: 600; color: ${theme?.text?.tertiary}; }
        .pw-toggle__btn.is-active {
          background: ${theme?.backgrounds?.card};
          border-color: ${theme?.borders?.primary};
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }
        .pw-toggle__btn--sub.is-active .pw-toggle__label { color: ${theme?.primary?.blue?.[500]}; }
        .pw-toggle__btn--once.is-active .pw-toggle__label { color: ${theme?.primary?.orange?.[500]}; }
        .pw-toggle__btn:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }

        /* Plan list */
        .pw-list { display: flex; flex-direction: column; gap: 10px; }

        /* Unselected rows are transparent + bordered (mobile PricingItem has no
           card fill); the border defines the edge against the page. */
        .pw-row {
          display: flex;
          align-items: center;
          gap: 14px;
          background: transparent;
          border: 1.5px solid ${theme?.borders?.primary};
          border-radius: 14px;
          padding: 14px 16px;
          cursor: pointer;
          transition: border-color 0.15s ease, background 0.15s ease;
        }
        .pw-row--info-only { cursor: default; }
        .pw-row:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }
        .pw-row--selected {
          border-color: ${accent};
          border-width: 2px;
          /* 0.12 alpha — matches mobile hexToRgba(accent, 0.12). */
          background: ${accent}1F;
        }

        .pw-row__radio {
          flex: 0 0 20px;
          width: 20px; height: 20px;
          border-radius: 50%;
          border: 2px solid ${theme?.borders?.primary};
          position: relative;
          transition: border-color 0.15s ease;
        }
        .pw-row--selected .pw-row__radio { border-color: ${accent}; }
        .pw-row--selected .pw-row__radio::after {
          content: '';
          position: absolute; inset: 3px;
          border-radius: 50%;
          background: ${accent};
        }

        .pw-row__body { flex: 1; min-width: 0; }
        .pw-row__top { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .pw-row__name { font-size: 1.05rem; font-weight: 800; color: ${theme?.text?.primary}; letter-spacing: -0.01em; }
        .pw-row__meta { font-size: 0.8rem; color: ${theme?.text?.tertiary}; margin-top: 3px; }
        /* [700] not [500] for AA contrast: green[500] on white is 2.28:1. The
           ramp is theme-inverted, so [700] is darker in light / lighter in dark
           and passes in both (5.97 / 13.13) — no brand-color value changes. */
        .pw-row__save { font-size: 0.78rem; font-weight: 700; color: ${theme?.primary?.green?.[700]}; margin-top: 5px; }
        .pw-row__rationale { font-size: 0.75rem; color: ${theme?.text?.tertiary}; margin-top: 4px; }

        .pw-badge {
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          padding: 3px 9px;
          border-radius: 100px;
          white-space: nowrap;
        }
        .pw-badge--popular {
          background: ${theme?.primary?.orange?.[500]}24;
          /* [700] text for AA on the 14% tint (orange[500] text was 1.88:1);
             theme-inverted ramp passes both themes (5.30 / 10.98). Tint kept. */
          color: ${theme?.primary?.orange?.[700]};
        }
        .pw-badge--best-value {
          background: ${theme?.primary?.blue?.[500]}24;
          /* [700] text for AA (same fix as the popular pill; [500] blue on the
             tint failed 4.14/3.30). Tint kept at [500]. */
          color: ${theme?.primary?.blue?.[700]};
        }

        .pw-row__price { flex: 0 0 auto; text-align: right; display: flex; flex-direction: column; align-items: flex-end; }
        .pw-row__orig {
          text-decoration: line-through;
          color: ${theme?.text?.tertiary};
          font-size: 0.85rem;
          font-weight: 600;
        }
        .pw-row__amount { font-size: 1.4rem; font-weight: 900; color: ${theme?.text?.primary}; line-height: 1.1; }
        /* A2: per-month equivalent under the total, smaller + muted. */
        .pw-row__permonth { font-size: 0.78rem; font-weight: 600; color: ${theme?.text?.tertiary}; margin-top: 2px; }

        /* CTA */
        .pw-cta-wrap { margin-top: 18px; }
        .pw-cta {
          width: 100%;
          border: none;
          border-radius: 13px;
          padding: 16px 14px;
          font-size: 1rem;
          font-weight: 800;
          letter-spacing: -0.01em;
          /* A6: white label on the blue button (text.inverse resolved dark in some
             themes). Matches mobile's white_absolute CTA text. */
          color: ${theme?.neutral?.white_absolute || '#ffffff'};
          cursor: pointer;
          background: ${accent};
          transition: filter 0.16s ease, transform 0.06s ease;
        }
        .pw-cta:hover { filter: brightness(1.05); }
        .pw-cta--disabled { opacity: 0.5; cursor: not-allowed; }
        .pw-cta-support {
          margin-top: 10px;
          text-align: center;
          font-size: 0.78rem;
          color: ${theme?.text?.tertiary};
        }
        /* A5: renewal/expiry legal disclosure + cancellation links. */
        .pw-legal { margin-top: 12px; text-align: center; }
        .pw-legal__text {
          margin: 0 0 6px;
          font-size: 0.72rem;
          line-height: 1.4;
          /* Required legal copy -> text.secondary for AA (parity with mobile B4;
             tertiary reintroduced the contrast issue that fix addressed). */
          color: ${theme?.text?.secondary};
        }
        .pw-legal__links { font-size: 0.72rem; }
        .pw-legal__link {
          color: ${theme?.primary?.blue?.[500]};
          text-decoration: none;
          padding: 6px 2px;
          /* reset so a <button> matches the <a> siblings */
          background: none;
          border: none;
          font: inherit;
          cursor: pointer;
        }
        .pw-legal__link:hover { text-decoration: underline; }
        .pw-legal__sep { color: ${theme?.text?.tertiary}; }

        /* How-to-cancel modal */
        .pw-cancel-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          z-index: 10000; padding: 1rem;
        }
        .pw-cancel-modal {
          position: relative;
          background: ${theme?.backgrounds?.card};
          border: 1px solid ${theme?.borders?.primary};
          border-radius: 16px;
          max-width: 460px; width: 100%;
          padding: 24px 22px;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.5);
          text-align: left;
        }
        .pw-cancel-close {
          position: absolute; top: 10px; right: 12px;
          background: none; border: none; cursor: pointer;
          font-size: 1.4rem; line-height: 1; color: ${theme?.text?.secondary};
          padding: 6px;
        }
        .pw-cancel-title { margin: 0 0 8px; font-size: 1.15rem; font-weight: 800; color: ${theme?.text?.primary}; }
        .pw-cancel-intro { margin: 0 0 12px; font-size: 0.95rem; color: ${theme?.text?.secondary}; }
        .pw-cancel-steps { margin: 0; padding-left: 1.3rem; }
        .pw-cancel-steps li { font-size: 1rem; color: ${theme?.text?.primary}; margin-bottom: 8px; line-height: 1.4; }

        .pricing-grid__secure-badge {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-top: 14px;
          font-size: 0.8rem;
          color: ${theme?.text?.tertiary};
        }
        .pricing-grid__secure-text { display: flex; align-items: center; gap: 8px; }
        .pricing-grid__secure-icon { color: ${theme?.primary?.green?.[500]}; }
        .pricing-grid__stripe-badge {
          background: #635bff;
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 700;
          font-size: 0.7rem;
        }

        @media (min-width: 769px) {
          .pricing-grid-container { padding: 28px 32px; }
          .pw-row { padding: 16px 20px; }
          .pw-row__amount { font-size: 1.55rem; }
        }
      `}</style>
    </div>
  );
};

export default PricingGrid;
